/**
 * A cópia do click id que sobrevive à navegação, guardada por nós.
 *
 * ## Por que existe
 *
 * Quem escreve o cookie `np_affiliate` é o SDK do TrackedX. Quando ele não escreve — e há mais de um
 * jeito de isso acontecer — o `?clickId=` da URL só serve enquanto o visitante estiver naquela URL:
 * qualquer navegação client-side depois disso troca a URL e o parceiro é perdido, mesmo tendo sido
 * ele a trazer a visita. O caso que dói é o visitante VER a campanha na landing (o leitor lê a URL
 * direto), converter, e a conversão não ser creditada a ninguém.
 *
 * ## Por que `sessionStorage` e não um cookie nosso
 *
 * Um cookie nosso seria um segundo cookie de afiliado, com dono, domínio e expiração diferentes do
 * `np_affiliate` — exatamente a segunda fonte de verdade que este desenho evita — e viajaria em todo
 * request HTTP. `sessionStorage` cobre o que se perde hoje (navegação, reload, volta de OAuth),
 * expira sozinho ao fim da aba (retenção mínima, nada a implementar) e é a solução que o repositório
 * já escolheu duas vezes para o mesmo problema: `src/utils/sso-trial-context.ts` e
 * `src/utils/user-tracking-data.ts`.
 *
 * ## Chave própria, e não `noping_utm_data`
 *
 * O bloco de UTM já guarda um `clickId`, mas `getUtmData()` SOBRESCREVE o storage inteiro sempre que
 * a URL traz qualquer UTM: um segundo pouso em `/pricing?utm_source=x` apaga o click id guardado. Os
 * dois dados também têm tempos de vida diferentes — UTM é "a última URL", click id é "quem trouxe
 * este visitante". Misturá-los foi o que criou aquela armadilha; esta chave não a herda.
 *
 * ## Primeira escrita vence
 *
 * Espelha o `setCookie()` do SDK, que só escreve quando não há cookie. Sem isso, um segundo pouso com
 * `?clickId=B` deixaria o store em `B` enquanto o cookie (que ganha na leitura) segue em `A` — o
 * store passaria a mentir em silêncio sobre o que a API vai atribuir.
 */
import { sanitizeClickId } from './sanitize-click-id';

/** Namespace próprio, separado de `noping_utm_data` de propósito (ver o bloco acima). */
const CLICK_ID_STORAGE_KEY = 'np_click_id';

/**
 * O click id guardado nesta aba, ou `undefined` — nunca lança.
 *
 * Sanitiza na leitura, e não só na escrita: o `sessionStorage` é editável pelo visitante, e o valor
 * volta a passar pelo mesmo filtro que a URL e o cookie passam.
 */
export function readStoredClickId(): string | undefined {
  if (typeof window === 'undefined') return undefined;

  try {
    return sanitizeClickId(sessionStorage.getItem(CLICK_ID_STORAGE_KEY));
  } catch {
    // Modo privado, storage desabilitado, cota estourada: seguir sem campanha é sempre melhor do que
    // derrubar a página que chamou.
    return undefined;
  }
}

/**
 * Guarda o click id, se ainda não houver um. Devolve o que ficou valendo.
 *
 * Não sobrescreve (ver "primeira escrita vence"), e um valor fora da forma não apaga o que já estava
 * guardado — quem chama passa o que leu da URL, e URL é território de terceiros.
 */
export function storeClickId(value: unknown): string | undefined {
  if (typeof window === 'undefined') return undefined;

  const clickId = sanitizeClickId(value);

  if (!clickId) return readStoredClickId();

  try {
    const stored = readStoredClickId();

    if (stored) return stored;

    sessionStorage.setItem(CLICK_ID_STORAGE_KEY, clickId);

    return clickId;
  } catch {
    return undefined;
  }
}

/** Usado pelos testes para isolar casos — o `jest.setup.ts` não limpa storage entre eles. */
export function clearStoredClickId(): void {
  if (typeof window === 'undefined') return;

  try {
    sessionStorage.removeItem(CLICK_ID_STORAGE_KEY);
  } catch {
    // sem storage não há o que limpar
  }
}
